/***************************************************
 * Created by gongyanyu on 2022/10/18 17:38. *
 ***************************************************/
import { action, computed, makeObservable } from 'mobx';
import { IPaginationSummary, IPaginationTrait } from '../interfaces/IPaginationTrait';
import { isViewModel } from '../helpers/isViewModel';
import { IModelConstructor } from '../interfaces/IModelConstructor';
import { handler, makeMetadata, property } from '../helpers/decorators';
import { BaseVM } from '../vms/BaseVM';

export function withPagination<T extends BaseVM = BaseVM>(Base: IModelConstructor<T>): IModelConstructor<IPaginationTrait<T> & T> {
  const BaseClass = Base as unknown as typeof BaseVM;

  if (!isViewModel(BaseClass)) {
    throw new Error('[VM]: This mixin can only decorate view model');
  }

  class WithPagination extends BaseClass implements IPaginationTrait<T> {
    constructor(...args: any[]) {
      super(...args);
      makeObservable(this);
      makeMetadata(this);
    }

    @property @computed get total() {
      return this.state.pagination?.total;
    }

    set total(total: number) {
      this.setTotal(total);
    }

    @property @computed get pageNo() {
      return this.state.pagination?.pageNo;
    }

    set pageNo(pageNo: number) {
      this.setPageNo(pageNo);
    }

    @property @computed get pageSize() {
      return this.state.pagination?.pageSize;
    }

    set pageSize(pageSize: number) {
      this.setPageSize(pageSize);
    }

    @action setTotal(total: number): this {
      this.setState({
        pagination: {
          ...this.state.pagination,
          total,
        },
      });
      return this;
    }

    @property @computed get pagination(): IPaginationSummary | false {
      const enable = this.state.enablePagination || this.state.enablePagination === undefined;
      return enable ? this.state.pagination : false;
    }

    set pagination(pagination: IPaginationSummary | false) {
      if (pagination === false) {
        this.setState({ enablePagination: false });
        return;
      }

      let { pageNo, current, total, pageSize } = { ...this.state.pagination, ...pagination } as IPaginationSummary;
      total = total ?? 0;
      current = pageNo = pageNo ?? current ?? 1;
      pageSize = pageSize ?? 10;
      pagination = { ...pagination, total, pageSize, pageNo, current };
      this.setState({ pagination, enablePagination: true });
    }

    @action setPagination(pagination: IPaginationSummary | false) {
      this.pagination = pagination;
    }

    @action setPageNo(pageNo: number): this {
      this.setState({
        pagination: {
          ...this.state.pagination,
          pageNo,
          current: pageNo,
        },
      });
      return this;
    }

    @action setPageSize(pageSize: number): this {
      this.setState({
        pagination: {
          ...this.state.pagination,
          pageSize,
        },
      });
      return this;
    }

    @handler @action onPaginationChange(pagination: IPaginationSummary | false) {
      this.pagination = pagination;
    }
  }

  return WithPagination as unknown as IModelConstructor<IPaginationTrait<T> & T>;
}
